import React, { memo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { util } from '../../../util'
import { Backdrop } from '../../common'
import { sideMenuOpened, sideMenuClosed } from '../../../modules/Common/actions'

/**
 * @returns {JSX} -> A component that exists inside the side menu
 */
const SideMenu = ({ open, direction, width, children, toggleMenu, showBackdrop = true }) => {
    const isSideMenuOpened = useSelector(({ common }) => common.is_side_menu_opened)
    const dispatch = useDispatch()
    const sideMenuRef = useRef({})
    const sideMenuClass = util.joinClasses(open ? 'side-menu open' : 'side-menu', direction, width)

    useEffect(() => {
        /* opening a side menu should not display the outer scroll bar */
        /* please check _base.scss to see styling to hide the scrollbar */
        if (open) {
            document.body.classList.add('hide-scroll-bar')
            !isSideMenuOpened && dispatch(sideMenuOpened())
        } else {
            document.body.classList.remove('hide-scroll-bar')
            isSideMenuOpened && dispatch(sideMenuClosed())
        }

        return () => {
            document.body.classList.remove('hide-scroll-bar')
            isSideMenuOpened && dispatch(sideMenuClosed())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <>
            {open && <Backdrop showOpacity={showBackdrop} closeBackdrop={toggleMenu} />}
            <div className={sideMenuClass} ref={sideMenuRef}>
                {open
                    ? React.cloneElement(children, {
                          sideMenuRef: sideMenuRef,
                      })
                    : null}
            </div>
        </>
    )
}

SideMenu.propTypes = {
    /** @param open {Boolean} -> property that determines whether to open the side menu or not */
    open: PropTypes.bool,

    /** @param direction {String} -> From which side that the side menu should come from (left or right) */
    direction: PropTypes.string,

    /** @param width {String} -> width of the side menu (sm, lg, xl, xxl) */
    width: PropTypes.oneOf(['xxl', 'lg', 'xl', 'sm']),

    /** @param children {JSX} -> wrapped Component */
    children: PropTypes.node,

    /** @param toggleMenu {callback} -> callback to close the side menu */
    toggleMenu: PropTypes.func,

    /** @param showBackdrop {Boolean} -> determines whether to show the opacity in backdrop */
    showBackdrop: PropTypes.bool,
}

export default memo(SideMenu)
